import { useCallback, useEffect, useRef, useState } from "react";
import { formatError } from "../../../../helpers/errors";

/**
 * Row-scoped publish/unpublish in-flight + failure tracking. Shared by
 * CalendarsPage and AlertsPage — was two identical hooks differing only by
 * the calendar/alert noun (`getCurrentData`/`updateItem`/`setItem` passed in
 * by the caller instead).
 *
 * Why this can't just read the list reducer's "row currently mutating"
 * field (e.g. `calendars.updateCalendarUUID`): it's reset back to "" on
 * BOTH the FULFILLED and REJECTED actions, so by the time a rejection
 * renders there's no reducer signal left saying *which* row just failed.
 * `pendingRef` remembers it independently of that reset.
 *
 * Also: the update action (e.g. `actions/calendar.js`'s updateCalendar())
 * never rejects its own returned promise — its .catch() dispatches the
 * REJECTED action but resolves normally either way — so a try/catch around
 * `await updateItem()` can't detect failure. Watching `item.updateError`/
 * `updateSuccess` via effect is the only way to observe the outcome.
 */
export function usePublishState({ item, updateItem, setItem, getCurrentData }) {
  const [pendingUuid, setPendingUuid] = useState(null);
  const [failedRow, setFailedRow] = useState(null); // { uuid, message, previousAvailable } | null
  const pendingRef = useRef(null);

  const togglePublish = useCallback(
    (targetItem) => {
      const previousAvailable = targetItem.available;
      const itemData = getCurrentData({
        ...targetItem,
        available: previousAvailable ? 0 : 1,
      });

      pendingRef.current = { uuid: targetItem.uuid, previousAvailable };
      setFailedRow((prev) => (prev?.uuid === targetItem.uuid ? null : prev));
      setPendingUuid(targetItem.uuid);
      updateItem(targetItem.uuid, itemData);
    },
    [updateItem, getCurrentData]
  );

  useEffect(() => {
    if (!pendingRef.current) {
      return;
    }

    if (item.updateError) {
      setFailedRow({
        uuid: pendingRef.current.uuid,
        message: formatError(item.updateError).join(" "),
        previousAvailable: pendingRef.current.previousAvailable,
      });
      setPendingUuid(null);
      pendingRef.current = null;
    } else if (item.updateSuccess) {
      setPendingUuid(null);
      pendingRef.current = null;
      setItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.updateError, item.updateSuccess]);

  const dismissError = useCallback(() => setFailedRow(null), []);

  return { pendingUuid, failedRow, togglePublish, dismissError };
}
